import { useShopFeaturesSWR } from 'hooks/shopify/swr/useShopFeaturesSWR';
import useMarketDrivenShippingStatus from 'hooks/useMarketDrivenShippingStatus';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useIntercom } from 'react-use-intercom';
import { useMarketDrivenShippingMigration } from './hooks/useMarketDrivenShippingMigration';
import { MarketDrivenShippingSwitchedBackBanner } from './MarketDrivenShippingSwitchedBackBanner';
import { MarketDrivenShippingMigrateConfirmModal } from './modals/confirm/MarketDrivenShippingMigrateConfirmModal';
import { MarketDrivenShippingMigratedInfoModal } from './modals/migrated/MarketDrivenShippingMigratedInfoModal';

// Shown wherever the ecom is in the MIGRATED market-driven-shipping stage, to explain why that page
// is read-only and point merchants at finishing their migration. Self-guards on that check, so
// callers can render it unconditionally — it renders nothing outside the MIGRATED stage, except for
// an ACTIVE ecom. A MIGRATED or ACTIVE ecom whose shop has switched market-driven shipping off in
// Shopify gets `MarketDrivenShippingSwitchedBackBanner` instead.
export const MarketDrivenShippingMigratedBanner: React.FC = () => {
    const { t } = useTranslation();
    const intercom = useIntercom();

    const { isMigrated: isMarketDrivenShippingMigrated, isActive: isMarketDrivenShippingActive } =
        useMarketDrivenShippingStatus();
    // Only a MIGRATED or ACTIVE ecom needs Shopify's own flag, so the request is skipped for LEGACY.
    const needsShopifyFlag = isMarketDrivenShippingMigrated || isMarketDrivenShippingActive;
    const { isMarketDrivenShippingEnabled } = useShopFeaturesSWR({ enabled: needsShopifyFlag });
    const {
        hasMigrated,
        confirmModalId,
        infoModalId,
        confirmModalRef,
        infoModalRef,
        handleConfirmMigration,
        handleGoToProviders,
    } = useMarketDrivenShippingMigration();

    // Strictly `false`: `undefined` means the shop features haven't loaded (or failed), not "off".
    if (needsShopifyFlag && isMarketDrivenShippingEnabled === false) {
        return <MarketDrivenShippingSwitchedBackBanner />;
    }

    if (!isMarketDrivenShippingMigrated) {
        return null;
    }

    return (
        <>
            {hasMigrated ? (
                <s-banner heading={t('MARKET_DRIVEN_SHIPPING_MIGRATED_BANNER.SUCCESS.HEADING')} tone="success">
                    <div className="flex flex-col gap-3">
                        <s-text>{t('MARKET_DRIVEN_SHIPPING_MIGRATED_BANNER.SUCCESS.DESCRIPTION')}</s-text>
                        <div className="flex gap-2">
                            <s-button variant="primary" onClick={handleGoToProviders}>
                                {t('MARKET_DRIVEN_SHIPPING_MIGRATED_BANNER.SUCCESS.BUTTON_LABEL')}
                            </s-button>
                        </div>
                    </div>
                </s-banner>
            ) : (
                <s-banner heading={t('MARKET_DRIVEN_SHIPPING_MIGRATED_BANNER.HEADING')} tone="warning">
                    <div className="flex flex-col gap-3">
                        <s-text>{t('MARKET_DRIVEN_SHIPPING_MIGRATED_BANNER.DESCRIPTION')}</s-text>
                        <div className="flex gap-2">
                            <s-button variant="primary" commandFor={confirmModalId} command="--show">
                                {t('MARKET_DRIVEN_SHIPPING_MIGRATED_BANNER.MIGRATE_BUTTON')}
                            </s-button>
                            <s-button onClick={() => intercom.showNewMessage()}>
                                {t('GENERAL_ERROR_BANNER.CONTACT_SUPPORT')}
                            </s-button>
                        </div>
                    </div>
                </s-banner>
            )}

            <MarketDrivenShippingMigrateConfirmModal
                id={confirmModalId}
                modalRef={confirmModalRef}
                onConfirm={handleConfirmMigration}
            />

            <MarketDrivenShippingMigratedInfoModal
                id={infoModalId}
                modalRef={infoModalRef}
                onGoToProviders={handleGoToProviders}
            />
        </>
    );
};
